import { useCallback, useEffect, useRef } from 'react';

import { i18n } from '@muzly/shared/i18n';

import { queueProfileSync, syncProfile } from '@muzly/api-client';

import { toProfileBody } from './toProfileBody';

import type { OnboardingAnswer } from '../onboarding/flow';

/** 연속 편집을 한 번의 PUT 으로 묶는 창. */
export const PROFILE_SYNC_DEBOUNCE_MS = 800;

/**
 * 바뀐 답을 서버에 반영한다 — **큐는 즉시, PUT 은 묶어서.**
 *
 * 지금 쓰는 곳은 진단 회차 설문(`OnboardingPage`, `delayMs = 0`)이다. 회차 답은 고른
 * 즉시 확정이라 묶을 것이 없다. 프로필 수정 화면은 저장 버튼을 갖게 되면서 이 훅을
 * 떠났다 — 거기서는 `queueProfileSync` 를 버튼이 직접 부른다.
 *
 * 큐에 쌓는 것은 localStorage 한 번이라 즉시 하고 — 그래야 창을 닫아도 답이 남는다 —
 * 네트워크만 늦춘다. 화면을 떠날 때 대기 중인 것이 있으면 나가면서 한 번 보낸다. 놓쳐도
 * 쌓는 순간 `useProfileSync` 가 깨어나 같은 큐를 비우므로 답이 사라지지는 않는다.
 */
export const useProfileAnswerSync = (
  delayMs: number = PROFILE_SYNC_DEBOUNCE_MS,
): ((answers: Record<string, OnboardingAnswer>) => void) => {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current === null) {
        return;
      }
      clearTimeout(timer.current);
      timer.current = null;
      void syncProfile();
    },
    [],
  );

  return useCallback(
    (answers: Record<string, OnboardingAnswer>) => {
      queueProfileSync(
        toProfileBody(answers, i18n.resolvedLanguage ?? i18n.language),
      );
      if (timer.current !== null) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(() => {
        timer.current = null;
        void syncProfile();
      }, delayMs);
    },
    [delayMs],
  );
};
